/**
 * Agent Detail Modal
 * UF-008: Agent Marketplace UI
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { MarketplaceAgent, AgentCapability, AgentReview } from "./types";
import { CATEGORY_CONFIG, PRICING_CONFIG } from "./types";

// =============================================================================
// Types
// =============================================================================

interface AgentDetailModalProps {
  agent: MarketplaceAgent | null;
  isOpen: boolean;
  onClose: () => void;
  onPurchase?: (agent: MarketplaceAgent) => void;
  onDeploy?: (agent: MarketplaceAgent) => void;
}

// =============================================================================
// Animation Variants
// =============================================================================

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 20,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.2,
    },
  },
};

const contentVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.1 + i * 0.05,
      duration: 0.4,
    },
  }),
};

// =============================================================================
// Icon Component
// =============================================================================

function AgentIcon({ icon, color, size = "lg" }: { icon?: string; color?: string; size?: "sm" | "lg" }) {
  const iconColor = color || "#758eff";
  const sizeClasses = size === "lg" ? "w-16 h-16" : "w-10 h-10";
  const iconSize = size === "lg" ? "w-8 h-8" : "w-5 h-5";

  const icons: Record<string, JSX.Element> = {
    brain: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24A2.5 2.5 0 0 1 9.5 2" />
        <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24A2.5 2.5 0 0 0 14.5 2" />
      </svg>
    ),
    "chart-line": (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 3v18h18" />
        <path d="m19 9-5 5-4-4-3 3" />
      </svg>
    ),
    "git-branch": (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <line x1="6" x2="6" y1="3" y2="15" />
        <circle cx="18" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
        <path d="M18 9a9 9 0 0 1-9 9" />
      </svg>
    ),
    "message-circle": (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
      </svg>
    ),
    shield: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      </svg>
    ),
    "eye-off": (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
        <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
        <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
        <path d="m2 2 20 20" />
      </svg>
    ),
    code: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
    settings: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  };

  return (
    <div
      className={`${sizeClasses} rounded-2xl flex items-center justify-center`}
      style={{
        background: `linear-gradient(135deg, ${iconColor}20, ${iconColor}10)`,
        border: `1px solid ${iconColor}30`,
      }}
    >
      <div className={iconSize} style={{ color: iconColor }}>
        {icons[icon || "brain"] || icons.brain}
      </div>
    </div>
  );
}

// =============================================================================
// Rating Stars Component
// =============================================================================

function RatingStars({ rating, size = "md" }: { rating: number; size?: "sm" | "md" | "lg" }) {
  const starSizes = { sm: "w-3.5 h-3.5", md: "w-4 h-4", lg: "w-5 h-5" };

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`${starSizes[size]} ${star <= Math.round(rating) ? "text-hvme-gold" : "text-hvme-text-muted/30"}`}
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

// =============================================================================
// Capability Item
// =============================================================================

function CapabilityItem({ capability, index }: { capability: AgentCapability; index: number }) {
  return (
    <motion.div
      custom={index}
      variants={contentVariants}
      initial="hidden"
      animate="visible"
      className="flex items-start gap-3 p-3 rounded-xl bg-hvme-bg-deep/50 border border-hvme-border/50"
    >
      <div className="w-8 h-8 rounded-lg bg-hvme-primary/10 flex items-center justify-center flex-shrink-0">
        <svg className="w-4 h-4 text-hvme-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <div>
        <h4 className="font-heading font-semibold text-sm text-hvme-text-primary">
          {capability.name}
        </h4>
        <p className="text-xs text-hvme-text-muted mt-0.5">
          {capability.description}
        </p>
      </div>
    </motion.div>
  );
}

// =============================================================================
// Review Card
// =============================================================================

function ReviewCard({ review, index }: { review: AgentReview; index: number }) {
  return (
    <motion.div
      custom={index + 5}
      variants={contentVariants}
      initial="hidden"
      animate="visible"
      className="p-4 rounded-xl bg-hvme-bg-deep/50 border border-hvme-border/50"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-hvme-primary/20 flex items-center justify-center">
            <span className="text-xs font-medium text-hvme-primary">
              {review.userName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-medium text-sm text-hvme-text-primary">
              {review.userName}
            </p>
            <p className="text-xs text-hvme-text-muted">
              {new Date(review.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
        <RatingStars rating={review.rating} size="sm" />
      </div>
      <p className="text-sm text-hvme-text-secondary leading-relaxed">
        {review.comment}
      </p>
      <div className="flex items-center gap-2 mt-3 text-xs text-hvme-text-muted">
        <button className="flex items-center gap-1 hover:text-hvme-text-secondary transition-colors">
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M7 10v12" />
            <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" />
          </svg>
          Helpful ({review.helpful})
        </button>
      </div>
    </motion.div>
  );
}

// =============================================================================
// Tab Button
// =============================================================================

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200
        ${active
          ? "text-hvme-primary"
          : "text-hvme-text-secondary hover:text-hvme-text-primary"
        }
      `}
    >
      {children}
      {active && (
        <motion.div
          layoutId="modal-tab-indicator"
          className="absolute bottom-0 left-2 right-2 h-0.5 bg-hvme-primary rounded-full"
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
        />
      )}
    </button>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function AgentDetailModal({
  agent,
  isOpen,
  onClose,
  onPurchase,
  onDeploy,
}: AgentDetailModalProps) {
  const [activeTab, setActiveTab] = useState<"overview" | "capabilities" | "reviews">("overview");

  if (!agent) return null;

  const categoryConfig = CATEGORY_CONFIG[agent.category];
  const pricingConfig = PRICING_CONFIG[agent.pricing.model];

  const formatPrice = () => {
    if (agent.pricing.model === "free") return "Free";
    if (!agent.pricing.price) return "Contact Sales";
    const period = agent.pricing.billingPeriod === "monthly" ? "/mo" : agent.pricing.billingPeriod === "yearly" ? "/yr" : "";
    return `$${agent.pricing.price}${period}`;
  };

  const formatDownloads = (count: number): string => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={onClose}
            className="absolute inset-0 bg-hvme-bg-deep/80 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="
              relative w-full max-w-3xl max-h-[90vh] overflow-hidden
              rounded-3xl bg-hvme-bg-surface border border-hvme-border
              shadow-2xl shadow-hvme-primary/10
            "
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="
                absolute top-4 right-4 z-20 w-8 h-8 rounded-full
                bg-hvme-bg-mid/80 border border-hvme-border
                flex items-center justify-center
                text-hvme-text-muted hover:text-hvme-text-primary
                transition-colors duration-200
              "
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>

            {/* Scrollable Content */}
            <div className="overflow-y-auto max-h-[90vh]">
              {/* Header */}
              <div className="p-6 pb-4 border-b border-hvme-border/50">
                <div className="flex items-start gap-5">
                  <AgentIcon icon={agent.icon} color={agent.iconColor} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className="font-heading font-bold text-2xl text-hvme-text-primary">
                          {agent.name}
                        </h2>
                        <div className="flex items-center gap-3 mt-2">
                          <span
                            className="px-2.5 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: `${categoryConfig.color}20`,
                              color: categoryConfig.color,
                            }}
                          >
                            {categoryConfig.label}
                          </span>
                          <span className="text-sm text-hvme-text-muted">
                            by {agent.publisher}
                            {agent.publisherVerified && (
                              <span className="ml-1 inline-flex items-center">
                                <svg className="w-3.5 h-3.5 text-hvme-primary" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="flex items-center gap-6 mt-4">
                      <div className="flex items-center gap-2">
                        <RatingStars rating={agent.rating} size="sm" />
                        <span className="text-sm font-medium text-hvme-text-primary">
                          {agent.rating.toFixed(1)}
                        </span>
                        <span className="text-sm text-hvme-text-muted">
                          ({agent.reviewCount} reviews)
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-hvme-text-muted">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" x2="12" y1="15" y2="3" />
                        </svg>
                        <span className="text-sm">{formatDownloads(agent.downloads)} downloads</span>
                      </div>
                      <span className="text-sm text-hvme-text-muted">
                        v{agent.version}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-1 mt-6">
                  <TabButton active={activeTab === "overview"} onClick={() => setActiveTab("overview")}>
                    Overview
                  </TabButton>
                  <TabButton active={activeTab === "capabilities"} onClick={() => setActiveTab("capabilities")}>
                    Capabilities
                  </TabButton>
                  <TabButton active={activeTab === "reviews"} onClick={() => setActiveTab("reviews")}>
                    Reviews ({agent.reviewCount})
                  </TabButton>
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                <AnimatePresence mode="wait">
                  {activeTab === "overview" && (
                    <motion.div
                      key="overview"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-6"
                    >
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-hvme-text-primary mb-3">
                          Description
                        </h3>
                        <div className="prose prose-invert prose-sm max-w-none">
                          <p className="text-hvme-text-secondary leading-relaxed whitespace-pre-line">
                            {agent.fullDescription}
                          </p>
                        </div>
                      </div>

                      {/* Tags */}
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-hvme-text-primary mb-3">
                          Tags
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {agent.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-3 py-1.5 rounded-full text-xs font-medium bg-hvme-bg-mid text-hvme-text-secondary border border-hvme-border"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Info Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-xl bg-hvme-bg-deep/50 border border-hvme-border/50">
                          <p className="text-xs text-hvme-text-muted mb-1">Last Updated</p>
                          <p className="text-sm font-medium text-hvme-text-primary">
                            {new Date(agent.lastUpdated).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="p-4 rounded-xl bg-hvme-bg-deep/50 border border-hvme-border/50">
                          <p className="text-xs text-hvme-text-muted mb-1">Published</p>
                          <p className="text-sm font-medium text-hvme-text-primary">
                            {new Date(agent.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === "capabilities" && (
                    <motion.div
                      key="capabilities"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-3"
                    >
                      {agent.capabilities.map((capability, index) => (
                        <CapabilityItem key={capability.id} capability={capability} index={index} />
                      ))}
                    </motion.div>
                  )}

                  {activeTab === "reviews" && (
                    <motion.div
                      key="reviews"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-4"
                    >
                      {agent.reviews.length > 0 ? (
                        agent.reviews.map((review, index) => (
                          <ReviewCard key={review.id} review={review} index={index} />
                        ))
                      ) : (
                        <div className="text-center py-12">
                          <p className="text-hvme-text-muted">No reviews yet</p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Footer / Actions */}
              <div className="sticky bottom-0 p-6 pt-4 border-t border-hvme-border/50 bg-hvme-bg-surface/95 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  {/* Price */}
                  <div>
                    <p className="text-xs text-hvme-text-muted mb-1">Pricing</p>
                    <div className="flex items-baseline gap-2">
                      <span className="font-heading font-bold text-2xl text-hvme-text-primary">
                        {formatPrice()}
                      </span>
                      {agent.pricing.model !== "free" && agent.pricing.billingPeriod && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${pricingConfig.badge}`}>
                          {agent.pricing.billingPeriod === "one-time" ? "One-time" : "Subscription"}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3">
                    {agent.owned ? (
                      agent.deployed ? (
                        <div className="flex items-center gap-3">
                          <span className="px-4 py-2 rounded-lg text-sm font-medium bg-hvme-green/10 text-hvme-green border border-hvme-green/20">
                            Deployed
                          </span>
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="
                              px-6 py-2.5 rounded-xl font-body text-sm font-semibold
                              bg-hvme-bg-mid text-hvme-text-secondary border border-hvme-border
                              hover:bg-hvme-bg-deep hover:text-hvme-text-primary
                              transition-all duration-200
                            "
                          >
                            Manage
                          </motion.button>
                        </div>
                      ) : (
                        <motion.button
                          whileHover={{
                            scale: 1.02,
                            boxShadow: "0 0 30px rgba(74, 255, 122, 0.4)",
                          }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => onDeploy?.(agent)}
                          className="
                            px-8 py-3 rounded-xl font-body text-sm font-semibold text-white
                            transition-all duration-300
                          "
                          style={{
                            background: "linear-gradient(135deg, #4aff7a, #00c853)",
                          }}
                        >
                          Deploy Agent
                        </motion.button>
                      )
                    ) : (
                      <motion.button
                        whileHover={{
                          scale: 1.02,
                          boxShadow: "0 0 30px rgba(117, 142, 255, 0.4), 0 0 60px rgba(214, 30, 244, 0.2)",
                        }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => onPurchase?.(agent)}
                        className="
                          relative px-8 py-3 rounded-xl font-body text-sm font-semibold text-white
                          overflow-hidden transition-all duration-300
                        "
                        style={{
                          background: "linear-gradient(135deg, #758eff, #d61ef4)",
                        }}
                      >
                        <span className="relative z-10">
                          {agent.pricing.model === "free" ? "Get Free" : agent.pricing.model === "subscription" ? "Subscribe" : "Purchase"}
                        </span>
                        <motion.div
                          className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent"
                          animate={{ x: ["0%", "200%"] }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            repeatDelay: 3,
                            ease: "linear",
                          }}
                        />
                      </motion.button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default AgentDetailModal;
